import { todayStr } from "../lib/date";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { getStudents, getAttendance, checkInStudents, checkOutStudents, getAbsences, recordAbsence, updateAttendanceStatus, getAlternatePlans, getRoster } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckInTab } from "../components/attendance/CheckInTab";
import { CheckOutTab } from "../components/attendance/CheckOutTab";
import { AbsencesTab } from "../components/attendance/AbsencesTab";
import { AttendanceSummary } from "../components/attendance/AttendanceSummary";
import { HeadCountTab } from "../components/attendance/HeadCountTab";
import { planActiveOnDate } from "../lib/altPlans";

export default function AttendancePage() {
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || todayStr();


  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(initialDate);

  // Check-in state
  const [onTimeCheckins, setOnTimeCheckins] = useState({});
  const [tardyStudent, setTardyStudent] = useState("");
  const [tardyTime, setTardyTime] = useState("");
  const [tardyType, setTardyType] = useState("unplanned");
  const [tardyExcused, setTardyExcused] = useState(true);
  const [tardyNotes, setTardyNotes] = useState("");

  // Check-out state
  const [onTimeCheckouts, setOnTimeCheckouts] = useState({});
  const [earlyStudent, setEarlyStudent] = useState("");
  const [earlyTime, setEarlyTime] = useState("");
  const [earlyPlanned, setEarlyPlanned] = useState(false);
  const [earlyNotes, setEarlyNotes] = useState("");

  // Absence state
  const [absenceStudent, setAbsenceStudent] = useState("");
  const [absenceType, setAbsenceType] = useState("excused");
  const [absenceNotes, setAbsenceNotes] = useState("");

  // Alternate plans
  const [alternatePlans, setAlternatePlans] = useState([]);
  // No-school detection
  const [roster, setRoster] = useState(null);
  const [showAnyway, setShowAnyway] = useState(false);

  const fetchData = async () => {
    try {
      const [studentsRes, attendanceRes, absencesRes, altRes, rosterRes] = await Promise.all([
        getStudents("current", false, selectedDate),
        getAttendance({ date: selectedDate }),
        getAbsences(selectedDate),
        getAlternatePlans({ active_only: true }),
        getRoster(selectedDate).catch(() => ({ data: null })),
      ]);
      setStudents(studentsRes.data.filter(s => !s.start_date || s.start_date <= selectedDate));
      setAttendance(attendanceRes.data);
      setAlternatePlans(altRes.data);
      setAbsences(absencesRes.data);
      setRoster(rosterRes.data);
    } catch (err) {
      toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); setShowAnyway(false); }, [selectedDate]);

  const getStudentScheduledTime = (student, type) => {
    const date = new Date(selectedDate + 'T00:00:00');
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[date.getDay()];
    const daySchedule = student.schedule?.[dayOfWeek];
    if (type === 'start') return daySchedule?.start || "08:30";
    return daySchedule?.end || "14:00";
  };

  const handleBulkCheckIn = async () => {
    const records = Object.entries(onTimeCheckins)
      .filter(([_, checked]) => checked)
      .map(([studentId]) => {
        const student = students.find(s => s.id === studentId);
        return {
          student_id: studentId, date: selectedDate,
          check_in_time: student ? getStudentScheduledTime(student, 'start') : "08:30",
          status: "present",
        };
      });
    if (records.length === 0) { toast.error("Please select at least one student"); return; }
    try {
      await checkInStudents(records);
      toast.success(`Checked in ${records.length} students`);
      setOnTimeCheckins({});
      fetchData();
    } catch { toast.error("Failed to check in students"); }
  };

  const handleTardyCheckIn = async () => {
    if (!tardyStudent) { toast.error("Please select a student"); return; }
    const now = new Date();
    const time = tardyTime || `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
    try {
      await checkInStudents([{
        student_id: tardyStudent, date: selectedDate, check_in_time: time,
        status: "tardy", tardy_type: tardyType, excused: tardyExcused, notes: tardyNotes,
      }]);
      toast.success("Tardy check-in recorded");
      setTardyStudent(""); setTardyTime(""); setTardyNotes("");
      fetchData();
    } catch { toast.error("Failed to record tardy"); }
  };

  const handleBulkCheckOut = async (isHalfDay = false) => {
    const records = Object.entries(onTimeCheckouts)
      .filter(([_, checked]) => checked)
      .map(([studentId]) => {
        const student = students.find(s => s.id === studentId);
        return {
          student_id: studentId, date: selectedDate,
          check_out_time: student ? getStudentScheduledTime(student, 'end') : (isHalfDay ? "12:00" : "14:00"),
        };
      });
    if (records.length === 0) { toast.error("Please select at least one student"); return; }
    try {
      await checkOutStudents(records);
      toast.success(`Checked out ${records.length} students`);
      setOnTimeCheckouts({});
      fetchData();
    } catch { toast.error("Failed to check out students"); }
  };

  const handleEarlyDismissal = async () => {
    if (!earlyStudent) { toast.error("Please select a student"); return; }
    const now = new Date();
    const time = earlyTime || `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
    try {
      await checkOutStudents([{
        student_id: earlyStudent, date: selectedDate, check_out_time: time,
        early_dismissal: true, planned: earlyPlanned, notes: earlyNotes,
      }]);
      toast.success("Early dismissal recorded");
      setEarlyStudent(""); setEarlyTime(""); setEarlyNotes("");
      fetchData();
    } catch { toast.error("Failed to record early dismissal"); }
  };

  const handleRecordAbsence = async () => {
    if (!absenceStudent) { toast.error("Please select a student"); return; }
    try {
      const formData = new URLSearchParams();
      formData.append("student_id", absenceStudent);
      formData.append("date", selectedDate);
      formData.append("absence_type", absenceType);
      formData.append("notes", absenceNotes);
      await recordAbsence(formData);
      toast.success("Absence recorded");
      setAbsenceStudent(""); setAbsenceNotes("");
      fetchData();
    } catch { toast.error("Failed to record absence"); }
  };

  const getStudentAttendance = (studentId) => attendance.find((a) => a.student_id === studentId);
  const displayName = (student) => (student.preferred_name || student.first_name);

  const getExpectedStudents = () => {
    const date = new Date(selectedDate + 'T00:00:00');
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[date.getDay()];
    const isHalfDay = !!roster?.is_half_day;
    const fullOutIds = getFullOutIds();
    return students.filter(s => {
      // Full day out of building: excused from the attendance prompt for the day.
      if (fullOutIds.has(s.id)) return false;
      const daySchedule = s.schedule?.[dayOfWeek];
      if (daySchedule && daySchedule.expected === false) return false;
      // Early-dismissal day: a student scheduled to arrive at/after the 12:00
      // dismissal can't attend — don't list them as expected. This covers both a
      // late weekly-schedule start AND a late alternate-plan arrival.
      if (isHalfDay) {
        const start = daySchedule?.start || "08:30";
        if (start >= "12:00") return false;
        const plan = (alternatePlans || []).find(
          p => p.student_id === s.id && !p.full_day_out && p.active !== false && planActiveOnDate(p, selectedDate)
        );
        const planArrival = plan?.arrival_time || plan?.effective_arrival || "";
        if (planArrival && planArrival >= "12:00") return false;
      }
      return true;
    });
  };

  // Students with an active "Full Day Out of Building" alternate plan on the
  // selected date — excused from marking, shown in a read-only info card instead.
  const getFullOutIds = () =>
    new Set(
      (alternatePlans || [])
        .filter(p => p.full_day_out && p.active !== false && planActiveOnDate(p, selectedDate))
        .map(p => p.student_id)
    );

  const getFullOutStudents = () => {
    const ids = getFullOutIds();
    return students
      .filter(s => ids.has(s.id))
      .map(s => {
        const plan = (alternatePlans || []).find(p => p.student_id === s.id && p.full_day_out && planActiveOnDate(p, selectedDate));
        return { ...s, _destination: plan?.destination || "" };
      })
      .sort((a, b) => displayName(a).localeCompare(displayName(b)));
  };

  const getUncheckedStudents = () => {
    const checkedIds = attendance.filter((a) => a.check_in_time).map((a) => a.student_id);
    return getExpectedStudents()
      .filter((s) => !checkedIds.includes(s.id))
      .sort((a, b) => displayName(a).localeCompare(displayName(b)) || a.last_name.localeCompare(b.last_name));
  };

  const getCheckedInStudents = () => {
    const checkedIds = attendance.filter((a) => a.check_in_time && !a.check_out_time).map((a) => a.student_id);
    return students
      .filter((s) => checkedIds.includes(s.id))
      .sort((a, b) => displayName(a).localeCompare(displayName(b)) || a.last_name.localeCompare(b.last_name));
  };

  const getSortedStudents = () => {
    return [...getExpectedStudents()].sort((a, b) => displayName(a).localeCompare(displayName(b)) || a.last_name.localeCompare(b.last_name));
  };

  const handleStatusChange = async (studentId, newStatus) => {
    try {
      // For tardy, auto-populate with current time
      let checkInTime = null;
      if (newStatus === "tardy") {
        const now = new Date();
        checkInTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
      }
      await updateAttendanceStatus(studentId, selectedDate, newStatus, checkInTime);
      const absentStatuses = ["absent_excused", "absent_unexcused", "absent", "excused", "unexcused"];
      if (absentStatuses.includes(newStatus)) {
        toast.success("Absence logged automatically");
      } else if (newStatus === "left_early_unexcused") {
        toast.success("Marked as left early");
      } else {
        toast.success("Status updated");
      }
      fetchData();
    } catch { toast.error("Failed to update status"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#00305E] tracking-tight">Attendance</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Manage daily attendance records</p>
          {roster?.is_school_day && roster?.is_half_day && (
            <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid="attendance-half-day-badge">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              Half Day — Dismissal 12:00 PM
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Label htmlFor="date" className="sr-only">Date</Label>
          <Input id="date" type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-auto" data-testid="date-picker" />
        </div>
      </div>

      {roster && roster.is_school_day === false && !showAnyway ? (
        <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-8 text-center space-y-3" data-testid="no-school-banner">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/></svg>
          </div>
          <h2 className="text-xl font-bold text-amber-900">No School Today</h2>
          <p className="text-amber-700">
            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            {roster.no_school_reason ? ` — ${roster.no_school_reason}` : ""}. No attendance needs to be recorded.
          </p>
          <Button variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => setShowAnyway(true)} data-testid="record-anyway-btn">
            Record attendance anyway
          </Button>
        </div>
      ) : (
      <Tabs defaultValue="checkin" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="checkin" data-testid="tab-checkin">Check In</TabsTrigger>
          <TabsTrigger value="checkout" data-testid="tab-checkout">Check Out</TabsTrigger>
          <TabsTrigger value="absences" data-testid="tab-absences">Absences</TabsTrigger>
          <TabsTrigger value="headcount" data-testid="tab-headcount">🔔 Head Count</TabsTrigger>
        </TabsList>

        <TabsContent value="checkin">
          <CheckInTab
            students={students} displayName={displayName}
            getUncheckedStudents={getUncheckedStudents} getSortedStudents={getSortedStudents}
            getStudentScheduledTime={getStudentScheduledTime}
            onTimeCheckins={onTimeCheckins} setOnTimeCheckins={setOnTimeCheckins}
            tardyStudent={tardyStudent} setTardyStudent={setTardyStudent}
            tardyTime={tardyTime} setTardyTime={setTardyTime}
            tardyType={tardyType} setTardyType={setTardyType}
            tardyExcused={tardyExcused} setTardyExcused={setTardyExcused}
            tardyNotes={tardyNotes} setTardyNotes={setTardyNotes}
            handleBulkCheckIn={handleBulkCheckIn} handleTardyCheckIn={handleTardyCheckIn}
          />
        </TabsContent>

        <TabsContent value="checkout">
          <CheckOutTab
            displayName={displayName} getCheckedInStudents={getCheckedInStudents}
            getStudentScheduledTime={getStudentScheduledTime}
            onTimeCheckouts={onTimeCheckouts} setOnTimeCheckouts={setOnTimeCheckouts}
            earlyStudent={earlyStudent} setEarlyStudent={setEarlyStudent}
            earlyTime={earlyTime} setEarlyTime={setEarlyTime}
            earlyPlanned={earlyPlanned} setEarlyPlanned={setEarlyPlanned}
            earlyNotes={earlyNotes} setEarlyNotes={setEarlyNotes}
            handleBulkCheckOut={handleBulkCheckOut} handleEarlyDismissal={handleEarlyDismissal}
          />
        </TabsContent>

        <TabsContent value="absences">
          <AbsencesTab
            displayName={displayName} getSortedStudents={getSortedStudents}
            absenceStudent={absenceStudent} setAbsenceStudent={setAbsenceStudent}
            absenceType={absenceType} setAbsenceType={setAbsenceType}
            absenceNotes={absenceNotes} setAbsenceNotes={setAbsenceNotes}
            handleRecordAbsence={handleRecordAbsence}
          />
        </TabsContent>

        <TabsContent value="headcount">
          <HeadCountTab />
        </TabsContent>
      </Tabs>
      )}

      {getFullOutStudents().length > 0 && (
        <div className="rounded-xl border border-[#00305E]/20 bg-[#00305E]/5 p-4" data-testid="out-of-building-card">
          <div className="flex items-center gap-2 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00305E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21V9a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v12"/><path d="M3 21h18"/><path d="M5 21V6a1 1 0 0 1 1-1h3"/></svg>
            <h3 className="font-semibold text-[#00305E] text-sm">Out of Building — Full Day ({getFullOutStudents().length})</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-2">These students are off-site all day (alternate plan) — no attendance needed. This day is excused and won't affect their attendance rate.</p>
          <div className="flex flex-wrap gap-2">
            {getFullOutStudents().map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-white border border-[#00305E]/20 text-[#00305E] text-xs px-2.5 py-1" data-testid={`out-of-building-${s.id}`}>
                {displayName(s)} {s.last_name}{s._destination ? ` · ${s._destination}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      <AttendanceSummary
        selectedDate={selectedDate} getSortedStudents={getSortedStudents}
        getStudentAttendance={getStudentAttendance} displayName={displayName}
        handleStatusChange={handleStatusChange} fetchData={fetchData}
        alternatePlans={alternatePlans}
      />
    </div>
  );
}
